import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Checkbox,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import configData from '../../configure';
import './FeePlannerModal.css';

const generateUUID = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const formatDateToISO = d => (d ? new Date(d).toISOString() : '');
const parseISOToLocalDate = iso => (iso ? iso.split('T')[0] : '');

const FeePlannerModal = ({
  open,
  onClose,
  editMode = false,
  selectedClass,
  selectedAcademicYear,
  classOptions = [],
  isSmallOrg = false,
  isViewOnlyYear = false,
  orgId,
  authAxios,
  token,
  onSaveSuccess,
}) => {
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [additionalClasses, setAdditionalClasses] = useState([]);
  const [expandedTerms, setExpandedTerms] = useState([0]);
  const [saving, setSaving] = useState(false);
  const [loadingModalData, setLoadingModalData] = useState(false);

  const FEE_CATEGORIES = useMemo(() => {
    return isSmallOrg
      ? ['Fee']
      : [
          'Admission Fee',
          'Tuition Fee',
          'SmartClass Fee',
          'Computer Fee',
          'Sports Fee',
          'Library Fee',
          'Extracurricular Fee',
          'Examination Fee',
        ];
  }, [isSmallOrg]);

  const [newFeeStructure, setNewFeeStructure] = useState({
    className: '',
    feePlanId: '',
    copyFromClass: '',
    academicYear: selectedAcademicYear,
    terms: [
      {
        description: 'Term 1',
        startDate: '',
        endDate: '',
        dueDate: '',
        feeBreakdown: isSmallOrg ? { Fee: '' } : {},
      },
    ],
  });

  // Initialize Modal Data when opened or mode changed
  useEffect(() => {
    if (!open) return;

    let isMounted = true;
    const initModal = async () => {
      if (editMode && selectedClass && selectedAcademicYear) {
        try {
          setLoadingModalData(true);
          const { data: currentData } = await authAxios.get(
            `${configData.GET_FEE_STRUCTURE}/class/${selectedClass}?academicYear=${selectedAcademicYear}`,
            { headers: { Authorization: `Bearer ${token}`, Accept: 'application/hal+json' } }
          );

          const rawCurrent = Array.isArray(currentData)
            ? currentData
            : (currentData?._embedded?.feeStructures || currentData?._embedded?.feeStructureList || currentData?.items || (currentData ? [currentData] : []));

          const currentFee = rawCurrent.find(i => i && i.academicYear === selectedAcademicYear) || rawCurrent[0];
          if (!currentFee) throw new Error('No fee structure found');

          const currentFeePlanId = currentFee.feePlanId;
          let sharedClassIds = [];
          if (currentFeePlanId) {
            try {
              const { data: allPlans } = await authAxios.get(
                `${configData.GET_FEE_STRUCTURE}/plan/${currentFeePlanId}?academicYear=${selectedAcademicYear}`,
                { headers: { Authorization: `Bearer ${token}`, Accept: 'application/hal+json' } }
              );
              const rawPlans = Array.isArray(allPlans)
                ? allPlans
                : (allPlans?._embedded?.feeStructures || allPlans?._embedded?.feeStructureList || allPlans?.items || (allPlans ? [allPlans] : []));

              sharedClassIds = rawPlans
                .filter(p => p && p.academicYear === selectedAcademicYear && p.feePlanId === currentFeePlanId)
                .map(p => p.classId);
            } catch (pErr) {
              console.warn('Failed to load shared fee plans:', pErr);
            }
          }

          const additionalSharedClasses = sharedClassIds.filter(id => id !== selectedClass);
          const className = classOptions.find(c => c.value === currentFee.classId)?.label || '';
          const terms = (currentFee.feeInstallments || []).filter(i => i.description !== 'Total Annual Fee');

          if (isMounted) {
            setNewFeeStructure({
              className,
              feePlanId: currentFeePlanId,
              copyFromClass: '',
              academicYear: currentFee.academicYear,
              terms: terms.map((t, idx) => {
                let fb = { ...t.feeBreakdown };
                if (isSmallOrg) {
                  const feeVal =
                    t.feeBreakdown?.Fee !== undefined && t.feeBreakdown?.Fee !== null
                      ? t.feeBreakdown.Fee
                      : t.totalFee !== undefined && t.totalFee !== null
                      ? t.totalFee
                      : t.feeBreakdown
                      ? Object.values(t.feeBreakdown).reduce((a, b) => Number(a || 0) + Number(b || 0), 0)
                      : '';
                  fb = { Fee: feeVal };
                }
                return {
                  description: t.description || `Term ${idx + 1}`,
                  startDate: parseISOToLocalDate(t.startDate),
                  endDate: parseISOToLocalDate(t.endDate),
                  dueDate: parseISOToLocalDate(t.dueDate),
                  feeBreakdown: fb,
                };
              }),
            });
            setAdditionalClasses(additionalSharedClasses);
            setExpandedTerms(terms.map((_, i) => i));
          }
        } catch (err) {
          console.error(err);
          toast.error('Failed to load fee structure for edit.');
        } finally {
          if (isMounted) setLoadingModalData(false);
        }
      } else {
        setSelectedClasses([selectedClass]);
        setAdditionalClasses([]);
        setNewFeeStructure({
          className: '',
          feePlanId: '',
          copyFromClass: '',
          academicYear: selectedAcademicYear,
          terms: [
            {
              description: 'Term 1',
              startDate: '',
              endDate: '',
              dueDate: '',
              feeBreakdown: isSmallOrg ? { Fee: '' } : {},
            },
          ],
        });
        setExpandedTerms([0]);
        setLoadingModalData(false);
      }
    };

    initModal();
    return () => {
      isMounted = false;
    };
  }, [open, editMode, selectedClass, selectedAcademicYear, authAxios, token, classOptions, isSmallOrg]);

  const handleInputChange = useCallback((e, termIdx, feeCat) => {
    const { name, value } = e.target;
    setNewFeeStructure(prev => {
      const terms = [...prev.terms];
      if (feeCat) {
        terms[termIdx].feeBreakdown[feeCat] = Number(value) || 0;
      } else {
        terms[termIdx][name] = value;
      }
      return { ...prev, terms };
    });
  }, []);

  const handleAddTerm = useCallback(() => {
    setNewFeeStructure(prev => {
      const nextIdx = prev.terms.length + 1;
      return {
        ...prev,
        terms: [
          ...prev.terms,
          {
            description: `Term ${nextIdx}`,
            startDate: '',
            endDate: '',
            dueDate: '',
            feeBreakdown: isSmallOrg ? { Fee: '' } : {},
          },
        ],
      };
    });
    setExpandedTerms(prev => [...prev, newFeeStructure.terms.length]);
  }, [isSmallOrg, newFeeStructure.terms.length]);

  const handleDeleteTerm = useCallback(idx => {
    setNewFeeStructure(prev => {
      if (prev.terms.length === 1) {
        toast.error('At least one term is required.');
        return prev;
      }
      const terms = prev.terms.filter((_, i) => i !== idx);
      return { ...prev, terms };
    });
    setExpandedTerms(prev => prev.filter(i => i !== idx));
  }, []);

  const handleCopyFromTerm = useCallback((targetIdx, sourceIdx) => {
    setNewFeeStructure(prev => {
      const terms = [...prev.terms];
      const src = terms[sourceIdx];
      terms[targetIdx].feeBreakdown = { ...src.feeBreakdown };
      toast.success(`Copied from ${src.description}`);
      return { ...prev, terms };
    });
  }, []);

  const handleCopyFeeStructure = useCallback(
    async copyClassId => {
      if (!copyClassId) return;
      try {
        const { data } = await authAxios.get(
          `${configData.GET_FEE_STRUCTURE}/class/${copyClassId}?academicYear=${selectedAcademicYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const src = data.find(s => s.academicYear === selectedAcademicYear);
        if (!src) throw new Error('No source');
        const copied = (src.feeInstallments || []).filter(i => i.description !== 'Total Annual Fee');
        setNewFeeStructure(prev => ({
          ...prev,
          copyFromClass: copyClassId,
          terms: copied.map(t => ({
            description: t.description,
            startDate: parseISOToLocalDate(t.startDate),
            endDate: parseISOToLocalDate(t.endDate),
            dueDate: parseISOToLocalDate(t.dueDate),
            feeBreakdown: { ...t.feeBreakdown },
          })),
        }));
        setExpandedTerms(copied.map((_, i) => i));
      } catch {
        toast.error('Copy failed.');
      }
    },
    [authAxios, selectedAcademicYear, token]
  );

  const termTotals = useMemo(() => {
    return newFeeStructure.terms.map(term => {
      if (isSmallOrg) {
        const val =
          term.feeBreakdown?.Fee !== undefined && term.feeBreakdown?.Fee !== null
            ? term.feeBreakdown.Fee
            : term.totalFee !== undefined && term.totalFee !== null
            ? term.totalFee
            : term.feeBreakdown
            ? Object.values(term.feeBreakdown).reduce((a, b) => Number(a || 0) + Number(b || 0), 0)
            : 0;
        return Number(val) || 0;
      }
      return Object.values(term.feeBreakdown || {}).reduce((s, v) => s + (Number(v) || 0), 0);
    });
  }, [newFeeStructure.terms, isSmallOrg]);

  const annualTotal = useMemo(() => {
    return termTotals.reduce((s, v) => s + v, 0);
  }, [termTotals]);

  const handleSaveFeeStructure = useCallback(async () => {
    if (isViewOnlyYear) {
      toast.error(`Cannot save changes for ${selectedAcademicYear}.`);
      return;
    }

    const classesToSave = editMode ? [selectedClass, ...additionalClasses] : selectedClasses;

    if (!classesToSave.length || !newFeeStructure.terms.length) {
      toast.error('Fill required fields.');
      return;
    }
    for (const t of newFeeStructure.terms) {
      if (!t.description || !t.startDate || !t.endDate || !t.dueDate) {
        toast.error('All term fields are required.');
        return;
      }
    }

    const feePlanId = newFeeStructure.feePlanId || generateUUID();

    try {
      setSaving(true);
      for (const classIdItem of classesToSave) {
        let existing = null;
        try {
          const res = await authAxios.get(
            `${configData.GET_FEE_STRUCTURE}/class/${classIdItem}?academicYear=${selectedAcademicYear}`,
            { headers: { Authorization: `Bearer ${token}`, Accept: 'application/hal+json' } }
          );
          existing = res.data.find(i => i.academicYear === selectedAcademicYear);
        } catch (err) {
          console.error(err);
        }

        const classDescription = classOptions.find(o => o.value === classIdItem)?.label || '';

        const payload = {
          feePlanId,
          academicYear: selectedAcademicYear,
          active: true,
          classId: classIdItem,
          description: classDescription,
          feeInstallments: newFeeStructure.terms.map(t => {
            const fb = {};
            Object.entries(t.feeBreakdown).forEach(([k, v]) => {
              if (v) fb[k] = Number(v);
            });
            const total = Object.values(fb).reduce((s, v) => s + v, 0);
            return {
              installmentId: generateUUID(),
              description: t.description,
              startDate: formatDateToISO(t.startDate),
              endDate: formatDateToISO(t.endDate),
              dueDate: formatDateToISO(t.dueDate),
              feeBreakdown: fb,
              totalFee: total,
              lateFee: 0,
            };
          }),
          orgId,
        };

        let url, method;
        if (existing) {
          payload.feeStructureId = existing.feeStructureId;
          method = authAxios.put;
          url = `${configData.GET_FEE_STRUCTURE}/${existing.feeStructureId}`;
        } else {
          delete payload.feeStructureId;
          method = authAxios.post;
          url = configData.GET_FEE_STRUCTURE;
        }

        const res = await method(url, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json, application/hal+json',
          },
        });

        const createdFeeStructureId = res.data?.feeStructureId || res.data?.id;
        if (createdFeeStructureId && !existing) {
          console.log('Created Fee Structure ID:', createdFeeStructureId);
        }
      }

      toast.success(`Fee structure ${editMode ? 'updated' : 'saved'} successfully.`);
      if (onSaveSuccess) onSaveSuccess(classesToSave);
      onClose();
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.message || err.response?.data?.error || (err.response?.status === 400 ? 'Validation error: Invalid classId or malformed academicYear.' : 'Save failed.');
      toast.error(typeof errMsg === 'string' ? errMsg : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [
    isViewOnlyYear,
    selectedAcademicYear,
    editMode,
    selectedClass,
    additionalClasses,
    selectedClasses,
    newFeeStructure,
    classOptions,
    orgId,
    authAxios,
    token,
    onSaveSuccess,
    onClose,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="fpm-dialog">
      <DialogTitle className="fpm-header">
        <div>
          <h2 className="fpm-header-title">
            {editMode
              ? `Edit Fee Planner – ${newFeeStructure.className || ''}`
              : `Create Fee Planner`}
          </h2>
          <p className="fpm-header-subtitle">
            {editMode ? 'Update the fee details below' : 'Fill the details below'}
          </p>
        </div>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="fpm-body">
        {loadingModalData ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <CircularProgress style={{ color: '#014486' }} />
          </div>
        ) : (
          <>
            {/* BASIC INFORMATION CARD */}
            <div className="fpm-card-section">
              <h3 className="fpm-section-title">Basic Information</h3>
              {editMode ? (
                <>
                  <div className="fpm-form-group">
                    <label className="fpm-label">Class Name</label>
                    <TextField value={newFeeStructure.className} fullWidth size="small" disabled />
                  </div>
                  <div className="fpm-form-group">
                    <label className="fpm-label">Apply to additional classes</label>
                    <Select
                      multiple
                      value={additionalClasses}
                      onChange={e => setAdditionalClasses(e.target.value)}
                      renderValue={selected =>
                        selected.map(id => classOptions.find(o => o.value === id)?.label || '').join(', ')
                      }
                      fullWidth
                      size="small"
                      disabled={true}
                    >
                      {classOptions
                        .filter(o => o.value !== selectedClass)
                        .map(o => (
                          <MenuItem key={o.value} value={o.value}>
                            <Checkbox checked={additionalClasses.includes(o.value)} />
                            <ListItemText primary={o.label} />
                          </MenuItem>
                        ))}
                    </Select>
                  </div>
                </>
              ) : (
                <div className="fpm-form-group">
                  <label className="fpm-label">Select Classes *</label>
                  <Select
                    multiple
                    value={selectedClasses}
                    onChange={e => setSelectedClasses(e.target.value)}
                    renderValue={selected =>
                      selected.map(id => classOptions.find(o => o.value === id)?.label || '').join(', ')
                    }
                    fullWidth
                    size="small"
                    disabled={isViewOnlyYear}
                  >
                    {classOptions.map(o => (
                      <MenuItem key={o.value} value={o.value}>
                        <Checkbox checked={selectedClasses.includes(o.value)} />
                        <ListItemText primary={o.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}

              {/* COPY FROM CLASS */}
              <div className="fpm-form-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <ContentCopyIcon style={{ fontSize: '18px', color: '#64748b' }} />
                  <label className="fpm-label" style={{ margin: 0 }}>
                    Copy Fee from Class
                  </label>
                </div>
                <Select
                  value={newFeeStructure.copyFromClass}
                  onChange={e => handleCopyFeeStructure(e.target.value)}
                  displayEmpty
                  fullWidth
                  size="small"
                  disabled={editMode || isViewOnlyYear}
                >
                  <MenuItem value="">
                    <em>Select to copy…</em>
                  </MenuItem>
                  {classOptions.map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>

            {/* TERM-WISE FEE STRUCTURE */}
            <div className="fpm-term-wise-section">
              <h3 className="fpm-section-title">Term-wise Fee Structure</h3>
              {newFeeStructure.terms.map((term, idx) => {
                const expanded = expandedTerms.includes(idx);
                const termTotal = termTotals[idx] || 0;
                return (
                  <div key={idx} className="fpm-term-card">
                    <div
                      className="fpm-term-header"
                      onClick={() =>
                        setExpandedTerms(prev =>
                          prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
                        )
                      }
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        <span className="fpm-term-title">{term.description}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span className="fpm-term-total-badge">Term Total: ₹{termTotal}</span>
                        {newFeeStructure.terms.length > 1 && !isViewOnlyYear && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteTerm(idx);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    </div>

                    {expanded && (
                      <div className="fpm-term-content">
                        {/* COPY FROM PREVIOUS TERM */}
                        {idx > 0 && (
                          <div
                            style={{
                              marginBottom: '16px',
                              padding: '12px',
                              backgroundColor: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                            }}
                          >
                            <div
                              style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                marginBottom: '8px',
                                color: '#475569',
                              }}
                            >
                              Copy fees from existing term:
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {newFeeStructure.terms.slice(0, idx).map((prev, pIdx) => (
                                <Button
                                  key={pIdx}
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleCopyFromTerm(idx, pIdx)}
                                  startIcon={<ContentCopyIcon style={{ fontSize: '15px' }} />}
                                  style={{
                                    textTransform: 'none',
                                    fontSize: '13px',
                                    padding: '4px 12px',
                                    borderRadius: '6px',
                                  }}
                                  disabled={isViewOnlyYear}
                                >
                                  {prev.description}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* TERM NAME */}
                        <div className="fpm-form-group">
                          <TextField
                            label="Term Name *"
                            name="description"
                            value={term.description}
                            onChange={e => handleInputChange(e, idx)}
                            fullWidth
                            size="small"
                            disabled={isViewOnlyYear}
                          />
                        </div>

                        {/* DATES GRID */}
                        <div className="fpm-date-grid">
                          <TextField
                            label="Start Date"
                            type="date"
                            name="startDate"
                            value={term.startDate}
                            onChange={e => handleInputChange(e, idx)}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            disabled={isViewOnlyYear}
                          />
                          <TextField
                            label="End Date"
                            type="date"
                            name="endDate"
                            value={term.endDate}
                            onChange={e => handleInputChange(e, idx)}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            disabled={isViewOnlyYear}
                          />
                          <TextField
                            label="Due Date"
                            type="date"
                            name="dueDate"
                            value={term.dueDate}
                            onChange={e => handleInputChange(e, idx)}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            disabled={isViewOnlyYear}
                          />
                        </div>

                        {/* FEE BREAKDOWN LIST */}
                        <div className="fpm-breakdown-box">
                          {isSmallOrg ? (
                            <div className="fpm-breakdown-row">
                              <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>
                                Fee *
                              </span>
                              <TextField
                                type="number"
                                placeholder="Enter fee (₹)"
                                value={
                                  term.feeBreakdown?.Fee !== undefined && term.feeBreakdown?.Fee !== null
                                    ? term.feeBreakdown.Fee
                                    : ''
                                }
                                onChange={e => handleInputChange(e, idx, 'Fee')}
                                size="small"
                                sx={{ width: '180px' }}
                                disabled={isViewOnlyYear}
                              />
                            </div>
                          ) : (
                            FEE_CATEGORIES.map(fee => (
                              <div key={fee} className="fpm-breakdown-row">
                                <span style={{ fontSize: '14px', color: '#334155' }}>{fee}</span>
                                <TextField
                                  type="number"
                                  placeholder="0"
                                  value={
                                    term.feeBreakdown[fee] !== undefined && term.feeBreakdown[fee] !== null
                                      ? term.feeBreakdown[fee]
                                      : ''
                                  }
                                  onChange={e => handleInputChange(e, idx, fee)}
                                  size="small"
                                  sx={{ width: '130px' }}
                                  disabled={isViewOnlyYear}
                                />
                              </div>
                            ))
                          )}
                        </div>

                        <div className="fpm-term-total-row">
                          <span>Term Total:</span>
                          <span>₹{termTotal}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {!isViewOnlyYear && (
                <Button
                  variant="outlined"
                  onClick={handleAddTerm}
                  fullWidth
                  sx={{
                    mt: 1,
                    textTransform: 'none',
                    borderRadius: '8px',
                    borderColor: '#cbd5e1',
                    color: '#014486',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#014486' },
                  }}
                >
                  + Add Term
                </Button>
              )}
            </div>

            {/* ANNUAL TOTAL BANNER */}
            <div className="fpm-annual-banner">
              <span className="fpm-annual-label">Annual Total (auto-calculated)</span>
              <span className="fpm-annual-amount">₹{annualTotal}</span>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions className="fpm-actions">
        <Button onClick={onClose} className="fpm-cancel-btn" disabled={saving}>
          Cancel
        </Button>
        {!isViewOnlyYear && (
          <Button
            onClick={handleSaveFeeStructure}
            className="fpm-save-btn"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : editMode ? 'Update' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(FeePlannerModal);
